import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ApiClient, createPartnersApi, getPartnerDetail } from '../services/api';
import { env } from '../config/env';
import { activeBrandingOverride } from '../config/branding';
import { useAuth } from './AuthContext';
import { usePartnerScope } from './PartnerScopeContext';

interface PartnerInfo {
  institutionName: string | null;
  logoUrl: string | null;
  portalLaunchDate: string | null;
  loading: boolean;
}

const PartnerContext = createContext<PartnerInfo | undefined>(undefined);

export function PartnerProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { idToken } = useAuth();
  const { activePartnerId: partnerId } = usePartnerScope();
  const partnersClient = useMemo(() => createPartnersApi(idToken), [idToken]);
  const backendClient = useMemo(() => new ApiClient(idToken, env.apiUrl), [idToken]);

  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [portalLaunchDate, setPortalLaunchDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partnerId == null || !idToken) {
      setInstitutionName(null);
      setLogoUrl(null);
      setPortalLaunchDate(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const detailPromise = getPartnerDetail(String(partnerId), partnersClient)
      .then((data) => {
        setInstitutionName(data.portal?.institution_name ?? data.program?.name ?? null);
        setLogoUrl(data.program?.logo_url ?? null);
      })
      .catch((err) => {
        console.error('[PartnerContext] Failed to fetch partner detail:', err);
      });
    const configPromise = backendClient
      .getPartnerPortalConfig(String(partnerId))
      .then((config) => {
        setPortalLaunchDate(config.portal_launch_date);
      })
      .catch((err) => {
        console.error('[PartnerContext] Failed to fetch portal config:', err);
      });
    Promise.allSettled([detailPromise, configPromise]).then(() => {
      setLoading(false);
    });
  }, [partnerId, idToken, partnersClient, backendClient]);

  // SOPHIA-35093: this is the single source point for partner branding, so the
  // staging mask applied here covers every surface (Dashboard header/logo,
  // Transcripts/Students subtitles) at once.
  const value = useMemo(() => {
    const branding = activeBrandingOverride();
    return {
      institutionName: branding?.institutionName ?? institutionName,
      logoUrl: branding?.hideLogo ? null : logoUrl,
      portalLaunchDate,
      loading,
    };
  }, [institutionName, logoUrl, portalLaunchDate, loading]);

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
}

export function usePartner(): PartnerInfo {
  const context = useContext(PartnerContext);
  if (context === undefined) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
}
